import React, { useState } from 'react';
import { Filter, RotateCcw, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { useGetUtilisateursQuery, useGetLogsAuditQuery } from '../../services/coreApi';
import type { LogAuditFilters, LogAuditItem } from '../../services/coreApi';

const OP_BADGE: Record<string, 'success' | 'info' | 'danger' | 'warning' | 'default'> = {
  creation: 'success',
  modification: 'info',
  suppression: 'danger',
  export: 'warning',
  rachat: 'warning',
  login: 'default',
  logout: 'default',
  calcul: 'info',
  validation: 'success',
  backup: 'warning',
};

const OP_TYPES = [
  { value: '', label: 'Tous' },
  { value: 'creation', label: 'Création' },
  { value: 'modification', label: 'Modification' },
  { value: 'suppression', label: 'Suppression' },
  { value: 'export', label: 'Export' },
  { value: 'rachat', label: 'Rachat' },
  { value: 'login', label: 'Connexion' },
  { value: 'logout', label: 'Déconnexion' },
  { value: 'calcul', label: 'Calcul' },
  { value: 'validation', label: 'Validation' },
  { value: 'backup', label: 'Backup' },
];

const PAGE_SIZE = 50;

const AuditLogTab: React.FC = () => {
  const { data: utilisateurs } = useGetUtilisateursQuery();
  const [filters, setFilters] = useState<LogAuditFilters>({});
  const [appliedFilters, setAppliedFilters] = useState<LogAuditFilters>({});
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data: logsData, isLoading, isFetching } = useGetLogsAuditQuery(appliedFilters);

  const logs = logsData?.results ?? [];
  const totalCount = logsData?.count ?? 0;
  const currentPage = appliedFilters.page || 1;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const applyFilters = () => {
    const clean: LogAuditFilters = { page: 1 };
    if (filters.utilisateur) clean.utilisateur = filters.utilisateur;
    if (filters.type_operation) clean.type_operation = filters.type_operation;
    if (filters.annee_academique) clean.annee_academique = filters.annee_academique;
    if (filters.table_concernee) clean.table_concernee = filters.table_concernee;
    setAppliedFilters(clean);
  };

  const resetFilters = () => {
    setFilters({});
    setAppliedFilters({});
  };

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setAppliedFilters(prev => ({ ...prev, page }));
  };

  const formatDate = (d: string) => {
    return new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-gray-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtres</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            value={filters.utilisateur ?? ''}
            onChange={(e) => setFilters({ ...filters, utilisateur: e.target.value ? parseInt(e.target.value) : undefined })}
          >
            <option value="">Tous les utilisateurs</option>
            {utilisateurs?.map(u => (
              <option key={u.id} value={u.id}>{u.nom} {u.prenoms} ({u.username})</option>
            ))}
          </select>
          <select
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            value={filters.type_operation ?? ''}
            onChange={(e) => setFilters({ ...filters, type_operation: e.target.value || undefined })}
          >
            {OP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <input
            type="text"
            placeholder="Année (ex: 2024-2025)"
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            value={filters.annee_academique ?? ''}
            onChange={(e) => setFilters({ ...filters, annee_academique: e.target.value || undefined })}
          />
          <input
            type="text"
            placeholder="Table (ex: notes_examens)"
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            value={filters.table_concernee ?? ''}
            onChange={(e) => setFilters({ ...filters, table_concernee: e.target.value || undefined })}
          />
        </div>
        <div className="flex gap-2 mt-3">
          <Button variant="primary" size="sm" icon={<Filter size={14} />} onClick={applyFilters}>Filtrer</Button>
          <Button variant="outline" size="sm" icon={<RotateCcw size={14} />} onClick={resetFilters}>Réinitialiser</Button>
          {isFetching && <Spinner size="sm" />}
        </div>
      </div>

      {/* Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {totalCount} entrée{totalCount > 1 ? 's' : ''} trouvée{totalCount > 1 ? 's' : ''}
        </p>
        {totalPages > 1 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Page {currentPage} sur {totalPages}
          </p>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Utilisateur</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Opération</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Table</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Détail</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">IP</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {logs.map((log: LogAuditItem) => {
                  const isExpanded = expandedId === log.id;
                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer"
                        onClick={() => setExpandedId(isExpanded ? null : log.id)}
                      >
                        <td className="px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400 font-mono whitespace-nowrap">
                          {formatDate(log.date_operation)}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300">{log.utilisateur_username || '—'}</td>
                        <td className="px-4 py-2.5">
                          <Badge variant={OP_BADGE[log.type_operation] || 'default'} size="sm">{log.type_operation}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-sm font-mono text-gray-600 dark:text-gray-400">{log.table_concernee || '—'}</td>
                        <td className="px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                          {log.justification || log.nouvelle_valeur?.substring(0, 60) || '—'}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-400 font-mono">{log.ip_address || '—'}</td>
                        <td className="px-2 py-2.5 text-center">
                          {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} className="px-4 py-3 bg-gray-50 dark:bg-gray-700/20">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Ancienne valeur</p>
                                <pre className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded text-xs font-mono text-gray-700 dark:text-gray-300 max-h-32 overflow-auto whitespace-pre-wrap">
                                  {log.ancienne_valeur || '(vide)'}
                                </pre>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Nouvelle valeur</p>
                                <pre className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded text-xs font-mono text-gray-700 dark:text-gray-300 max-h-32 overflow-auto whitespace-pre-wrap">
                                  {log.nouvelle_valeur || '(vide)'}
                                </pre>
                              </div>
                              {log.justification && (
                                <div className="md:col-span-2">
                                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">Justification</p>
                                  <p className="text-gray-700 dark:text-gray-300">{log.justification}</p>
                                </div>
                              )}
                              <div className="md:col-span-2 text-xs text-gray-400">
                                ID enregistrement: {log.id_enregistrement ?? '—'} | Année: {log.annee_academique || '—'}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      Aucune entrée dans le journal d'audit
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            icon={<ChevronLeft size={14} />}
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            Précédent
          </Button>
          <span className="text-sm text-gray-600 dark:text-gray-400 px-3">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            Suivant <ChevronRight size={14} className="ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default AuditLogTab;
